import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { getAccessToken } from './api.js';

/**
 * Socket da corrida.
 *
 * O token é lido a cada (re)conexão, não capturado uma vez: o access token
 * dura 15 minutos e a conexão dura o turno inteiro do motorista. Passar o
 * valor fixo faria a reconexão de daqui a uma hora usar um token morto.
 */
export function useSocket(enabled: boolean): { socket: Socket | null; connected: boolean } {
  const ref = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [, force] = useState(0);

  useEffect(() => {
    if (!enabled) {
      ref.current?.disconnect();
      ref.current = null;
      setConnected(false);
      return;
    }

    const socket = io({
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: (cb) => cb({ token: getAccessToken() }),
      reconnection: true,
      reconnectionDelay: 1_000,
      reconnectionDelayMax: 10_000,
    });

    ref.current = socket;
    force((n) => n + 1);

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      ref.current = null;
    };
  }, [enabled]);

  return { socket: ref.current, connected };
}

/** Assina um evento e limpa sozinho. */
export function useSocketEvent<T = unknown>(
  socket: Socket | null,
  event: string,
  handler: (payload: T) => void,
): void {
  const saved = useRef(handler);
  saved.current = handler;

  useEffect(() => {
    if (!socket) return;
    const fn = (payload: T) => saved.current(payload);
    socket.on(event, fn);
    return () => {
      socket.off(event, fn);
    };
  }, [socket, event]);
}
